import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useTranslate } from 'src/locales';
import { paths } from 'src/routes/paths';
import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import {
  useGetCandidateDocumentsQuery,
  useGetOrganizationCandidateQuery,
  useUpdateCandidateRatingMutation,
} from 'src/services/candidates/candidates.service';
import { ISalaryExpectation } from 'src/services/candidates/candidates.types';
import { DocumentType } from 'src/services/files/file.types';
import { EmailMessageTemplateType, MailerTargetType } from 'src/services/email-sync/email-sync.types';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

import AvailabilityStatusLabel from 'src/components/candidates/availability-status-label';
import DocumentViewer from 'src/components/documents/document-viewer';
import StarRating from 'src/components/rating/star-rating';

import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';
import { useCommunicationActions, buildDialNumber } from 'src/sections/admin/integrations/communications/use-communication-actions';
import AddCandidateJobModal from 'src/sections/jobs/modals/add-candidate-job-modal';
import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import CandidateDocumentsView from 'src/sections/candidates/detail/sections/candidate-documents';
import CandidateProfileEditor from 'src/sections/profile/profile-sections/profile-editor';

import RecordPanelHeader from '../record-panel-header';
import RecordActionBar from '../record-action-bar';
import { RecordAction, RecordHeaderModel, RecordMetaItem } from '../record-panel.types';

// ----------------------------------------------------------------------

enum TabValue {
  PROFILE = 'profile',
  RESUME = 'resume',
  DOCUMENTS = 'documents',
}

const formatSalary = (s: ISalaryExpectation, t: (k: string, o?: any) => string): string => {
  const fmt = (n: number) => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${n}`);
  const period = t(`enums.period_type.${s.period?.toLowerCase?.() ?? s.period}`, { defaultValue: '' });
  return `$${fmt(s.min_amount)}–$${fmt(s.max_amount)}${period ? ` / ${period}` : ''}`;
};

/**
 * Display-only tidy of the parsed resume_text: joins a bullet marker that the parser left stranded on
 * its own line onto the text that follows, and collapses runs of blank lines. Purely cosmetic — the
 * stored text is never modified.
 */
const tidyResumeText = (text: string): string =>
  text
    .replace(/^[ \t]*([•·‣▪])[ \t]*\r?\n+[ \t]*(?=\S)/gm, '$1 ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/**
 * Candidate body for the record panel (JT1-1529). Fetches the CRM record and feeds the shared header
 * (avatar, name + availability, job title, and a location/salary/notice/rank metadata row) and action
 * bar (Add to job, Add to list, Send email, Call — all labelled buttons). The body defaults to the
 * resume: the parsed `resume_text` when present, falling back to the PDF viewer, then the profile.
 */
export default function CandidateRecordView({ id }: { id: string }) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const tenantType = useOrgTenant();
  const { featureEnabled } = useFeatureFlags();

  const { data: candidate, isLoading } = useGetOrganizationCandidateQuery(id, { skip: !id });
  const profile = candidate?.candidate_profile;

  // Resume lives on the ORG-CANDIDATE document store (organization_candidate_documents), same source
  // the full detail page uses — not the profile store (cp_documents), which is empty for candidates
  // whose CV was never cloned onto the attached profile (e.g. pre-item-33 records). Filter to the
  // resume type server-side.
  const { data: resumeDocs } = useGetCandidateDocumentsQuery(
    { candidate_id: id, params: { page: 1, per_page: 1, type: DocumentType.RESUME } },
    { skip: !id }
  );
  const resumeDoc = resumeDocs?.results?.[0];

  const hasResumeText = Boolean(candidate?.resume_text);
  const hasResume = hasResumeText || Boolean(resumeDoc);

  const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.PROFILE);
  // Default to the resume once we know one exists; otherwise the profile.
  useEffect(() => {
    if (!candidate) return;
    setCurrentTab(hasResume ? TabValue.RESUME : TabValue.PROFILE);
  }, [candidate, hasResume]);

  const [updateRating] = useUpdateCandidateRatingMutation();
  const handleRating = useCallback(async (rating: number) => {
    try {
      await updateRating({ candidateId: id, rating }).unwrap();
      enqueueSnackbar(t('candidates.api.update_rating.success'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('candidates.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [updateRating, id, t]);

  // Actions
  const addToJob = useBoolean();
  const addToList = useBoolean();

  const commsName = `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim();
  const commsNumber = candidate?.privileged_access ? buildDialNumber(candidate?.phone_number ?? undefined, candidate?.country_code ?? undefined) : undefined;
  const comms = useCommunicationActions(commsNumber, commsName, { entity_type: CommunicationEntityType.CANDIDATE, entity_id: id });

  const canEmail = featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED) && tenantType !== TenantType.Candidate && Boolean(candidate?.email);
  const handleSendEmail = useCallback(() => {
    if (!candidate?.email) return;
    dispatch(openSingleMailerDialog({
      emails: [candidate.email],
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      templateEntityId: id,
      targetType: MailerTargetType.CANDIDATE,
      targetId: id,
    }));
  }, [candidate?.email, dispatch, id]);

  const actions: RecordAction[] = useMemo(() => [
    { key: 'add-to-job', label: t('common.add_to_job'), icon: 'mdi:invite', onClick: addToJob.onTrue },
    { key: 'add-to-list', label: t('common.add_to_list', { defaultValue: 'Add to list' }), icon: 'solar:list-bold', onClick: addToList.onTrue },
    { key: 'send-email', label: t('common.send_email'), icon: 'lsicon:email-send-filled', onClick: handleSendEmail, hidden: !canEmail },
    { key: 'call', label: t('integrations.communications.actions.call'), icon: 'solar:phone-bold', onClick: () => comms.call(), hidden: !comms.commsActive },
  ], [t, addToJob.onTrue, addToList.onTrue, handleSendEmail, canEmail, comms]);

  const header: RecordHeaderModel = useMemo(() => {
    const meta: RecordMetaItem[] = [];
    if (profile?.location) meta.push({ key: 'location', label: t('candidates.table.location', { defaultValue: 'Location' }), value: profile.location });
    const salary = profile?.salary_expectations?.[0];
    if (salary) meta.push({ key: 'salary', label: t('candidates.table.salary_range', { defaultValue: 'Salary range' }), value: formatSalary(salary, t) });
    if (profile?.notice_period?.type) meta.push({ key: 'notice', label: t('candidates.table.notice_period', { defaultValue: 'Notice period' }), value: t(`enums.notice_period_type.${profile.notice_period.type}`, { defaultValue: profile.notice_period.type }) });
    meta.push({ key: 'rank', label: t('candidates.table.rating'), value: <StarRating rating={candidate?.rating || 0} onSelectExcitementRating={handleRating} /> });

    return {
      avatarUrl: profile?.profile_image?.public_path,
      name: `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim() || t('common.loading', { defaultValue: 'Loading…' }),
      subtitle: profile?.position_title,
      status: profile?.availability_status ? <AvailabilityStatusLabel status={profile.availability_status} /> : undefined,
      meta,
      contact: candidate?.privileged_access
        ? { email: candidate?.email, phone: candidate?.phone_number ? `${candidate.country_code ?? ''} ${candidate.phone_number}`.trim() : null }
        : undefined,
      fullProfileHref: paths.dashboard.candidates.detail(id),
    };
  }, [profile, candidate, t, handleRating, id]);

  // Resume body: the uploaded document (PDF viewer) first so its original formatting is preserved,
  // falling back to the parsed resume_text for candidates with no viewable file (migrated /
  // extracted-only), then an empty message (spec item 14; JT1-1529). When search-term highlighting
  // lands (item 14 / JT1-1537) this will prefer the text view when arriving from a search.
  const renderResume = () => {
    if (resumeDoc) {
      return (
        <Box sx={{ height: '100%' }}>
          <DocumentViewer fileUrl={resumeDoc.document.public_path} />
        </Box>
      );
    }
    if (hasResumeText) {
      return (
        <Box
          sx={{
            whiteSpace: 'pre-wrap',
            typography: 'body2',
            lineHeight: 1.8,
            color: 'text.primary',
            p: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          {tidyResumeText(candidate?.resume_text ?? '')}
        </Box>
      );
    }
    return (
      <Typography sx={{ py: 5 }} align="center" variant="body2">
        {t('candidates.drawer.no_resume')}
      </Typography>
    );
  };

  if (isLoading || !candidate) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <RecordPanelHeader model={header} />
      <RecordActionBar actions={actions} />

      <Tabs
        value={currentTab}
        onChange={(_e, tab) => setCurrentTab(tab)}
        sx={{ px: 2.5, mt: 1, boxShadow: (theme) => `inset 0 -2px 0 0 ${theme.palette.divider}` }}
      >
        <Tab value={TabValue.PROFILE} label={t('candidates.drawer.tabs.profile', { defaultValue: 'Profile' })} />
        {hasResume && <Tab value={TabValue.RESUME} label={t('candidates.drawer.tabs.resume', { defaultValue: 'Resume' })} />}
        <Tab value={TabValue.DOCUMENTS} label={t('candidates.drawer.tabs.documents', { defaultValue: 'Documents' })} />
      </Tabs>

      {currentTab === TabValue.PROFILE && (
        <Stack sx={{ px: 3, py: 2 }}>
          <CandidateProfileEditor profile={profile} isLoading={isLoading} mode="view" showNavigation={false} commsEntityId={candidate.id} />
        </Stack>
      )}

      {currentTab === TabValue.RESUME && (
        <Stack sx={{ px: 3, py: 2, flexGrow: 1, minHeight: 0 }}>{renderResume()}</Stack>
      )}

      {currentTab === TabValue.DOCUMENTS && (
        <Stack sx={{ px: 3, py: 2 }}>
          <CandidateDocumentsView candidateId={id} uploadVisible={false} />
        </Stack>
      )}

      <AddCandidateJobModal open={addToJob.value} onClose={addToJob.onFalse} candidateId={id} />
      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={addToList.onFalse} items={[id]} entityType={ListEntityType.CANDIDATE} />
      {comms.smsModal}
    </>
  );
}
